import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  ServerError,
  createHttpLink,
  fromPromise,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLFormattedError } from "graphql";

export interface CreateApolloClientOptions {
  uri: string;
  /** Resolves to a valid id token, or null when logged out. */
  getToken: () => Promise<string | null>;
  /** Refreshes after the API rejected `rejectedToken`; null if the session ended. */
  forceRefresh: (rejectedToken?: string) => Promise<string | null>;
  /** Shows an error to the user. */
  reportError: (message: string) => void;
}

interface AuthContext {
  headers?: Record<string, string>;
  /** The id token this request was sent with (null when anonymous). */
  authToken?: string | null;
  /** Set once the request has been retried after a token rejection. */
  authRetried?: boolean;
}

/** Hasura reports a bad/expired JWT as error code `invalid-jwt`. */
const AUTH_ERROR_CODES = new Set(["invalid-jwt", "invalid-headers"]);

function isAuthError(
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
  networkError: Error | undefined | null
): boolean {
  const serverError = networkError as ServerError | undefined;
  const bodyErrors =
    (serverError?.result as { errors?: GraphQLFormattedError[] } | undefined)
      ?.errors ?? [];
  const errors = [...(graphQLErrors ?? []), ...bodyErrors];
  return (
    serverError?.statusCode === 401 ||
    errors.some((e) => AUTH_ERROR_CODES.has(e.extensions?.code as string))
  );
}

export function createApolloClient({
  uri,
  getToken,
  forceRefresh,
  reportError,
}: CreateApolloClientOptions): ApolloClient<NormalizedCacheObject> {
  // Shows errors to the user, once per distinct message. Runs outermost, so a
  // request that succeeded after a token retry reports nothing.
  const reportErrorsLink = onError(({ graphQLErrors, networkError }) => {
    if (isAuthError(graphQLErrors, networkError)) {
      reportError("We couldn't verify your login. Please log in again.");
      return;
    }
    const messages = new Set((graphQLErrors ?? []).map((e) => e.message));
    for (const message of messages) reportError(message);
    if (networkError && messages.size === 0) reportError("Network error!");
  });

  // If the API rejects the token, refresh once and retry the request. If the
  // refresh token is also rejected the session is expired (the user is logged
  // out with a message) and the retry goes out anonymously.
  const retryWithFreshTokenLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      const { authToken, authRetried } = operation.getContext() as AuthContext;
      if (!authToken || authRetried || !isAuthError(graphQLErrors, networkError)) {
        return;
      }
      operation.setContext({ authRetried: true });
      return fromPromise(forceRefresh(authToken).catch(() => null)).flatMap(
        () => forward(operation)
      );
    }
  );

  // Adds `Authorization: Bearer <id token>` when logged in. Runs on every
  // attempt, so a retry picks up the refreshed token (or none).
  const authLink = setContext(async (_, previous: AuthContext) => {
    const token = await getToken();
    const headers = Object.fromEntries(
      Object.entries(previous.headers ?? {}).filter(
        ([name]) => name.toLowerCase() !== "authorization"
      )
    );
    return token
      ? { authToken: token, headers: { ...headers, Authorization: `Bearer ${token}` } }
      : { authToken: null, headers };
  });

  return new ApolloClient({
    link: ApolloLink.from([
      reportErrorsLink,
      retryWithFreshTokenLink,
      authLink,
      createHttpLink({ uri }),
    ]),
    cache: new InMemoryCache(),
  });
}
